import {AsyncPriceCourseCardContext, BrowseCourseBadgesContext} from '@udemy/browse-course';
import {
    PAGE_TYPE_LOGGED_OUT_HOMEPAGE,
    PAGE_TYPE_PERSONALIZED_LOGGED_OUT_HOMEPAGE,
} from '@udemy/discovery-api';
import {useMatchMedia} from '@udemy/hooks';
import {
    CourseUnit,
    CourseUnitSkeletonVerticalLayout,
    DiscoveryUnitsContainer,
    CourseDetailsQuickViewBoxContext,
} from '@udemy/react-discovery-units';
import {isFirstTimeVisitor} from '@udemy/shared-utils';

import {
    browseCourseContextProps,
    minimalBrowseCourseContextProps,
} from '../../lib/browseContextPropsConfig';
import styles from './discovery-units.module.less';

export const DiscoveryUnits = ({
    isVerticalRecoUnitsFeatureEnabled,
    showAddToCartButtonsInVerticalLayout,
    showReimagineLohp,
}: {
    isVerticalRecoUnitsFeatureEnabled?: boolean;
    showAddToCartButtonsInVerticalLayout?: boolean;
    showReimagineLohp?: boolean;
}) => {
    const hasCoarsePointer = useMatchMedia('(any-pointer: coarse)');
    const hasFinePointer = useMatchMedia('(any-pointer: fine)');
    const isMobileMax = useMatchMedia('mobile-max');

    const showVerticalRecoUnits = isVerticalRecoUnitsFeatureEnabled && !!isMobileMax;

    const stackedLayoutProps = {
        layout: 'singlecol' as const,
        showStackedProductCardLayout: true,
        showTitle: true,
        disableInfiniteScroll: true,
        showAddToCartInCourseCard: showAddToCartButtonsInVerticalLayout || false,
        addToCartButtonStyle: 'primary' as const,
        addToCartButtonSize: 'xsmall' as const,
        courseCardProps: {
            layout: 'vertical',
            showCourseTag: false,
            showDescription: false,
            showDetails: false,
            showRibbonsLast: true,
            disablePremiumHighlightedRibbon: true,
        },
        showInstructorDetails: true,
        isMobileMax: isMobileMax ?? undefined,
        showQuickViewBox: false,
        maxCardsPerStack: 3,
        maxCards: 3,
        showDividers: false,
        loadingSkeleton: <CourseUnitSkeletonVerticalLayout />,
    };

    const reimagineLohpLayoutProps = {
        isMobileMax: isMobileMax ?? undefined,
        courseCardProps: {
            layout: 'vertical',
            showCourseTag: false,
            showDescription: false,
            showDetails: false,
            ribbonHighlighted: false,
        },
        disableInfiniteScroll: isMobileMax ?? undefined,
        showQuickViewBox: !isMobileMax,
        maxCardsPerStack: 3,
        maxCards: 3,
        showDividers: !isMobileMax,
        showStackedProductCardLayout: isMobileMax ?? undefined,
        layout: isMobileMax ? 'singlecol' : 'singlerow',
        gridCols: !isMobileMax ? 4 : undefined,
        titleTypography: 'ud-heading-xxl ud-text-responsive',
    };

    const getUnitComponentAndProps = () => {
        return {
            component: CourseUnit,
            componentProps: {
                renderExperimentalCourseCards: false,
                layout: 'singlerow',
                titleTypography: 'ud-heading-serif-xxl ud-text-responsive',
                showPager: !!hasFinePointer,
                allowScroll: !!hasCoarsePointer,
                ...(showVerticalRecoUnits && stackedLayoutProps),
                ...(showReimagineLohp && reimagineLohpLayoutProps),
            },
        };
    };

    return (
        <BrowseCourseBadgesContext.Provider
            value={
                showVerticalRecoUnits ? browseCourseContextProps : minimalBrowseCourseContextProps
            }
        >
            <CourseDetailsQuickViewBoxContext.Provider value={{showAiGeneratedHighlights: true}}>
                <AsyncPriceCourseCardContext.Provider
                    value={
                        showVerticalRecoUnits || showReimagineLohp
                            ? {
                                  useCourseProductCard: showVerticalRecoUnits || showReimagineLohp,
                                  hideCourseDescription: showVerticalRecoUnits || showReimagineLohp,
                              }
                            : {}
                    }
                >
                    <DiscoveryUnitsContainer
                        className={styles['discovery-unit']}
                        itemCount={18}
                        pageType={
                            isFirstTimeVisitor()
                                ? PAGE_TYPE_LOGGED_OUT_HOMEPAGE
                                : PAGE_TYPE_PERSONALIZED_LOGGED_OUT_HOMEPAGE
                        }
                        getUnitComponentAndProps={getUnitComponentAndProps}
                    />
                </AsyncPriceCourseCardContext.Provider>
            </CourseDetailsQuickViewBoxContext.Provider>
        </BrowseCourseBadgesContext.Provider>
    );
};
